import { Box, Grid, Typography, Select, Option } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { useLabor } from "../hooks/useLabor";
import { LaborCard } from "./LaborCard";
import { LaborSkeleton } from "../skeletons";
import { SupplierComparisonRefresh as SupplierComparison } from "../../..";
import { Wrench, Plus } from "lucide-react";
import { OPTIONAL_LABOR_ITEMS } from "../hooks/useLaborMasterDataInit";

interface LaborProps {
    measurements: any[];
}

export default function Labor({ measurements }: LaborProps) {
    const {
        laborSchema,
        selectedValues,
        adaptedSelectedValues,
        priceRanges,
        isLoading,
        handleQuantityUpdate,
        handleClearLabor,
        handleReevaluateFormula,
        allLaborServices,
        handleAddLaborItem,
    } = useLabor({ measurements });

    const optionalOptions = allLaborServices.filter(
        (item: any) => OPTIONAL_LABOR_ITEMS.includes(item.Name) && !laborSchema.some((schemaItem: any) => schemaItem.Id === item.Id)
    );

    return (
        <AnimatePresence mode="popLayout">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <LaborSkeleton />
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box>
                        <Box mb={2}>
                            <Typography level="title-md" startDecorator={<Wrench size={16} />}>
                                Labor & Service Scopes
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Crew assignments, installation services, and project labor rates.
                            </Typography>
                        </Box>

                        {laborSchema.length === 0 ? (
                            <Box
                                p={4}
                                mb={2}
                                textAlign="center"
                                sx={{ border: "1px dashed", borderColor: "divider", borderRadius: "md" }}
                            >
                                <Typography level="body-sm" color="neutral">
                                    No labor items identified for this project.
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={1.5}>
                                {laborSchema.map((item: any) => (
                                    <Grid xs={12} key={item.Id}>
                                        <LaborCard
                                            laborItem={item}
                                            selection={selectedValues[item.Id]}
                                            priceRange={priceRanges[item.Id]}
                                            onQuantityChange={(serviceId, direction) =>
                                                handleQuantityUpdate(serviceId, direction)
                                            }
                                            onRemove={(serviceId) => handleClearLabor(serviceId)}
                                            onReevaluate={(serviceId: string) => handleReevaluateFormula(serviceId)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {optionalOptions.length > 0 && (
                            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
                                <Select
                                    placeholder="Add Additional Service..."
                                    value=""
                                    onChange={(e, val) => {
                                        if (val) handleAddLaborItem(val);
                                    }}
                                    startDecorator={<Plus size={16} />}
                                    sx={{
                                        minWidth: 260,
                                        borderRadius: "lg",
                                        boxShadow: "sm",
                                    }}
                                    slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                                >
                                    {optionalOptions.map((option: any) => (
                                        <Option key={option.Id} value={option.Id}>
                                            {option.Name}
                                        </Option>
                                    ))}
                                </Select>
                            </Box>
                        )}

                        {/* Labor Supplier Comparison & Selection */}
                        <Box mt={4}>
                            <SupplierComparison type="LABOR" selectedValues={adaptedSelectedValues} />
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
